import React, { useEffect, useState } from "react";
import { db } from "../../firebase";
import { ref, get, set } from "firebase/database";
import { TbBottleFilled } from "react-icons/tb";
import { GiWaterGallon } from "react-icons/gi";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logo from "../../assets/logo.jpg";

export default function Profile() {
  /* ===================== STATES ===================== */
  const [userData, setUserData] = useState(null);
  const [entries, setEntries] = useState([]);
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [qrAmount, setQrAmount] = useState(0);
  const [qrUrl, setQrUrl] = useState("");
  const [paymentMode, setPaymentMode] = useState(null);
  const [paymentRequestStatus, setPaymentRequestStatus] = useState(null);
  const [paidVia, setPaidVia] = useState(null);
  const [adminUpiId, setAdminUpiId] = useState("");

  const loggedUser = JSON.parse(localStorage.getItem("loggedInUser"));

  /* ===================== CONSTANTS ===================== */
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const years = Array.from({ length: 15 }, (_, i) => 2023 + i);

  /* ===================== GENERATE QR ===================== */
  const generateQR = () => {
    if (!adminUpiId) {
      alert("Admin UPI not available");
      return;
    }

    if (totalAmount <= 0) {
      alert("No bill amount found!");
      return;
    }

    setPaymentRequestStatus(null);
    setQrAmount(totalAmount.toFixed(2));

    const url = `upi://pay?pa=${adminUpiId}&pn=Refresh+H2O&am=${totalAmount}&cu=INR`;

    setQrUrl(
      `https://api.qrserver.com/v1/create-qr-code/?size=260x260&data=${encodeURIComponent(
        url
      )}`
    );
  };

  useEffect(() => {
    const fetchUpi = async () => {
      try {
        const snap = await get(ref(db, "settings/upi"));
        if (snap.exists()) {
          setAdminUpiId(snap.val().upiId);
        }
      } catch (e) {
        console.error("UPI fetch failed", e);
      }
    };

    fetchUpi();
  }, []);

  /* ===================== I PAID ===================== */

  const markAsPaidRequest = async () => {
    if (paymentRequestStatus === "PENDING") return;

    try {
      const userKey = loggedUser.userId;
      const monthKey = `${months[selectedMonth - 1].slice(
        0,
        3
      )} ${selectedYear}`;

      await set(ref(db, `paymentRequests/${userKey}/${monthKey}`), {
        userId: loggedUser.userId,
        userName: loggedUser.name,
        month: monthKey,
        amount: totalAmount,

        status: "PENDING", // ✅ user only sets PENDING
        paymentMode: null, // ✅ admin will fill
        paymentDate: new Date().toISOString(),

        approvedAt: null,
        rejectedAt: null,
      });

      setPaymentRequestStatus("PENDING");
      alert("✅ Payment request sent. Waiting for admin approval.");
    } catch (err) {
      console.error(err);
      alert("❌ Failed to send payment request");
    }
  };

  /* ===================== FETCH USER ===================== */
  useEffect(() => {
    if (!loggedUser) return;

    const fetchUser = async () => {
      try {
        const snap = await get(ref(db, `users/${loggedUser.firebaseKey}`));
        if (snap.exists()) setUserData(snap.val());
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [loggedUser]);

  /* ===================== FETCH ENTRIES ===================== */
  useEffect(() => {
    if (!loggedUser) return;

    const fetchEntries = async () => {
      const paths = [
        loggedUser.firebaseKey,
        loggedUser.userId,
        loggedUser.name?.replace(/\s+/g, "_"),
        loggedUser.name,
      ].filter(Boolean);

      for (const path of paths) {
        const snap = await get(ref(db, `entries/${path}`));
        if (snap.exists()) {
          const arr = Object.keys(snap.val()).map((k) => ({
            id: k,
            ...snap.val()[k],
          }));
          arr.sort((a, b) => new Date(b.date) - new Date(a.date));
          setEntries(arr);
          return;
        }
      }
      setEntries([]);
    };

    fetchEntries();
  }, [loggedUser]);

  /* ===================== FILTER ===================== */
  useEffect(() => {
    const filtered = entries.filter((e) => {
      const d = new Date(e.date);
      return (
        d.getMonth() + 1 === Number(selectedMonth) &&
        d.getFullYear() === Number(selectedYear)
      );
    });
    setFilteredEntries(filtered);
  }, [entries, selectedMonth, selectedYear]);

  /* ===================== TOTALS ===================== */
  const totalJars = filteredEntries.reduce(
    (s, e) => s + Number(e.jarQty || 0),
    0
  );
  const totalCaps = filteredEntries.reduce(
    (s, e) => s + Number(e.capsuleQty || 0),
    0
  );
  const totalAmount = filteredEntries.reduce(
    (s, e) => s + Number(e.totalPrice || 0),
    0
  );

  /* ===================== PAYMENT STATUS ===================== */
  useEffect(() => {
    if (!loggedUser) return;

    const userKey = loggedUser.userId;
    const monthKey = `${months[selectedMonth - 1].slice(0, 3)} ${selectedYear}`;

    get(ref(db, `paymentRequests/${userKey}/${monthKey}`)).then((snap) => {
      if (snap.exists()) {
        const data = snap.val();

        setPaymentRequestStatus(data.status); // PENDING / SUCCESS / REJECTED
        setPaidVia(data.paymentMode || null); // UPI / CASH
        setPaymentMode(data.paymentMode || null);

        // ✅ SUCCESS → QR hide
        if (data.status === "SUCCESS") {
          setQrUrl("");
        }

        // 🔁 PENDING → QR regenerate on refresh
        if (data.status === "PENDING" && adminUpiId) {
          const url = `upi://pay?pa=${adminUpiId}&pn=Refresh+H2O&am=${data.amount}&cu=INR`;

          setQrAmount(Number(data.amount).toFixed(2));
          setQrUrl(
            `https://api.qrserver.com/v1/create-qr-code/?size=260x260&data=${encodeURIComponent(
              url
            )}`
          );
        }
      } else {
        setPaymentRequestStatus(null);
        setPaymentMode("UNPAID");
        // setQrUrl("");
      }
    });
  }, [loggedUser, selectedMonth, selectedYear, adminUpiId]);

  const downloadPDF = () => {
    if (!filteredEntries.length) {
      alert("No data");
      return;
    }

    // ===== PRICES FROM FIREBASE (NUMBER) =====
    const jarPrice = Number(userData?.jarPrice || 0);
    const capsulePrice = Number(userData?.capsulePrice || 0);

    const doc = new jsPDF("p", "mm", "a4");

    /* ================= LOGO ================= */
    doc.addImage(logo, "PNG", 25, 6, 30, 30);

    /* ================= COMPANY HEADER ================= */
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text("REFRESH H2O WATER SERVICES", 105, 18, { align: "center" });

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text("Address: Vijay Nagar, Indore - 452001", 105, 24, {
      align: "center",
    });
    doc.text(
      "Contact: +91 99262 02603 | Email: Refreshh2o.0056@gmail.com",
      105,
      29,
      { align: "center" }
    );

    doc.line(14, 34, 196, 34);

    /* ================= TOP DETAILS ================= */

    // ===== BILL DETAILS (LEFT) =====
    doc.setFont("helvetica", "bold");
    doc.text("BILL DETAILS", 14, 40);

    doc.setFont("helvetica", "normal");
    doc.text(`Bill No: BILL-${Date.now().toString().slice(-4)}`, 14, 46);
    doc.text(`Bill Date: ${new Date().toLocaleDateString()}`, 14, 52);
    doc.text(
      `Bill Month: ${months[selectedMonth - 1]} ${selectedYear}`,
      14,
      58
    );

    // ===== PRICE DETAILS (CENTER) =====
    doc.setFont("helvetica", "bold");
    doc.text("PRICE DETAILS", 75, 40);

    doc.setFont("helvetica", "normal");
    doc.text(`Jar Price: Rs ${jarPrice}`, 75, 46);
    doc.text(`Capsule Price: Rs ${capsulePrice}`, 75, 52);

    // ===== CUSTOMER DETAILS (RIGHT) =====
    doc.setFont("helvetica", "bold");
    doc.text("CUSTOMER DETAILS", 130, 40);

    doc.setFont("helvetica", "normal");
    doc.text(`Name: ${userData?.name || ""}`, 130, 46);
    doc.text(`Contact: ${userData?.contact || ""}`, 130, 52);
    doc.text(`Address: ${userData?.address || ""}`, 130, 58, {
      maxWidth: 60,
    });

    // Divider lines
    doc.setDrawColor(220);
    doc.line(70, 38, 70, 60);
    doc.line(120, 38, 120, 60);

    /* ================= TABLE ================= */
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("MONTHLY USAGE DETAILS", 14, 65);

    autoTable(doc, {
      startY: 70,
      head: [["DATE", "JAR QTY", "CAPSULE QTY", "TOTAL PRICE (RS)"]],
      body: filteredEntries
        .slice(0, 31)
        .map((e) => [
          new Date(e.date).toLocaleDateString(),
          e.jarQty,
          e.capsuleQty,
          ` ${e.totalPrice} /-`,
        ]),
      styles: {
        fontSize: 9,
        cellPadding: 2,
        halign: "center",
        valign: "middle",
      },
      headStyles: {
        fillColor: [3, 184, 179],
        textColor: 255,
        fontStyle: "bold",
        minCellHeight: 6,
      },
      bodyStyles: {
        minCellHeight: 6,
      },
      pageBreak: "avoid", // 🔥 31 rows same page
    });

    /* ================= SUMMARY ================= */
    const finalY = doc.lastAutoTable.finalY + 12;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("SUMMARY", 14, finalY);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(`TOTAL JARS: ${totalJars}`, 14, finalY + 6);
    doc.text(`TOTAL CAPSULES: ${totalCaps}`, 14, finalY + 12);
    doc.text(`SUB TOTAL:  ${totalAmount} /-`, 14, finalY + 18);

    doc.setFont("helvetica", "bold");
    doc.text(`GRAND TOTAL: ${totalAmount} /-`, 14, finalY + 24);

    /* ================= PAYMENT DETAILS ================= */
    doc.setFont("helvetica", "bold");
    doc.text("PAYMENT DETAILS", 130, finalY);

    doc.setFont("helvetica", "normal");
    doc.text(`MODE: ${paymentMode || "UNPAID"}`, 130, finalY + 6);

    let yPos = finalY + 12;

    if (paymentMode === "CASH") {
      doc.setFont("helvetica", "bold");
      doc.text("PAYMENT RECEIVED IN CASH", 130, yPos);
      yPos += 6;
    }

    if (paymentMode !== "CASH" && adminUpiId) {
      doc.setFont("helvetica", "normal");
      doc.text(`UPI ID: ${adminUpiId}`, 130, yPos);
      yPos += 6;
    }

    let statusText = "UNPAID";
    if (paymentRequestStatus === "SUCCESS") statusText = "PAID";
    if (paymentRequestStatus === "PENDING") statusText = "PENDING";
    if (paymentRequestStatus === "REJECTED") statusText = "REJECTED";

    doc.setFont("helvetica", "bold");
    doc.text(`STATUS: ${statusText}`, 130, yPos);

    /* ================= FOOTER ================= */
    doc.line(14, 290, 196, 290);
    doc.setFontSize(9);
    doc.text("• This is a computer-generated bill", 14, 295);

    /* ================= SAVE ================= */
    doc.save(`Bill_${months[selectedMonth - 1]}_${selectedYear}.pdf`);
  };

  /* ===================== LOADER ===================== */
  if (loading) {
    return (
      <>
        <style>{`
        .loader-wrapper {
          height: 70vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }

        .spinner {
          width: 48px;
          height: 48px;
          border: 5px solid #e0f2f1;
          border-top: 5px solid #03b8b3;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .loader-text {
          margin-top: 14px;
          font-weight: 700;
          color: #022c32;
          font-size: 15px;
        }
      `}</style>

        <div className="loader-wrapper">
          <div className="spinner" />
          <div className="loader-text">Loading profile...</div>
        </div>
      </>
    );
  }
  /* ===================== MAIN RETURN ===================== */

  return (
    <>
      <style>{`
        .profile-wrapper {
          padding: 16px 12px;
          max-width: 900px;
          margin: auto;
          animation: fadeIn 0.4s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .profile-header {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(12px);
          border-radius: 16px;
          padding: 20px 16px;
          margin-bottom: 20px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.08);
          text-align: center;
        }

        .avatar {
          width: 70px;
          height: 70px;
          border-radius: 50%;
          background: linear-gradient(135deg, #03b8b3, #04d4cd);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 28px;
          color: #fff;
          font-weight: 700;
          margin: auto;
          box-shadow: 0 4px 15px rgba(3,184,179,0.3);
        }

        .profile-name {
          font-size: 22px;
          font-weight: 800;
          margin-top: 10px;
          color: #022c32;
          letter-spacing: 0.5px;
        }

        .info-line {
          margin: 4px 0;
          color: #555;
          font-size: 14px;
          line-height: 1.4;
        }

        .status-badge {
          padding: 5px 12px;
          border-radius: 20px;
          font-weight: 700;
          color: white;
          display: inline-block;
          font-size: 13px;
          margin-top: 5px;
        }

        .status-active {
          background: linear-gradient(135deg, #4caf50, #66bb6a);
        }

        .status-inactive {
          background: linear-gradient(135deg, #e53935, #ff5252);
        }

        .highlight-cards {
          display: flex;
          justify-content: center;
          gap: 12px;
          flex-wrap: wrap;
          margin-bottom: 20px;
        }

        .highlight-box {
          padding: 14px 16px;
          min-width: 110px;
          background: #ffffff;
          border-radius: 12px;
          border: 1px solid #e0f4f3;
          box-shadow: 0 3px 8px rgba(3,184,179,0.15);
          font-size: 15px;
          font-weight: 700;
          color: #022c32;
          display: flex;
          align-items: center;
          gap: 8px;
          flex: 1;
          min-width: 140px;
          justify-content: center;
        }

        .filter-card {
          background: #fff;
          padding: 16px 12px;
          margin-top: 8px;
          border-radius: 14px;
          display: flex;
          flex-direction: column;
          gap: 12px;
          box-shadow: 0 3px 10px rgba(3,184,179,0.12);
        }

        .filter-row {
          display: flex;
          gap: 10px;
          width: 100%;
        }

        .filter-select {
          padding: 10px 12px;
          border-radius: 10px;
          border: 1px solid #bcd6db;
          font-size: 14px;
          font-weight: 600;
          color: #022c32;
          flex: 1;
        }

        .section-title {
          font-size: 20px;
          font-weight: 800;
          margin: 20px 0 12px 0;
          color: #022c32;
          text-align: center;
        }

        .table-container {
          overflow-x: auto;
          margin-top: 12px;
          border-radius: 14px;
          box-shadow: 0 3px 10px rgba(0,0,0,0.08);
        }

        table.history {
          width: 100%;
          border-collapse: collapse;
          background: white;
          min-width: 400px;
        }

        table.history th {
          background: #03b8b3;
          color: white;
          padding: 10px 8px;
          font-size: 14px;
          font-weight: 700;
          text-transform: uppercase;
        }

        table.history td {
          padding: 10px 8px;
          border-bottom: 1px solid #edf3f3;
          font-weight: 600;
          font-size: 14px;
          text-align: center;
        }

        .totals-box {
          margin-top: 16px;
          background: #f1fafa;
          padding: 16px;
          border-radius: 14px;
          font-size: 15px;
          font-weight: 700;
          color: #022c32;
          box-shadow: 0 3px 10px rgba(3,184,179,0.12);
          text-align: center;
        }

        .payment-section {
          margin-top: 20px;
          padding: 18px 16px;
          border-radius: 16px;
          background: #fff;
          box-shadow: 0 3px 10px rgba(0,0,0,0.08);
          text-align: center;
        }

        @media (min-width: 768px) {
          .profile-wrapper {
            padding: 24px 20px;
          }
          .filter-card {
            flex-direction: row;
            align-items: center;
          }
          .filter-row {
            flex: 1;
          }
          .highlight-box {
            flex: none;
          }
        }
      `}</style>

      <div className="profile-wrapper">
        {/* Profile Card */}
        <div className="profile-header">
          <div className="avatar">{userData?.name?.[0]?.toUpperCase()}</div>
          <div className="profile-name">{userData?.name}</div>

          <p className="info-line">{userData?.contact}</p>
          <p className="info-line">ID: {userData?.userId}</p>
          <p className="info-line">Joined: {userData?.joiningDate || "N/A"}</p>
          <p className="info-line">Address: {userData?.address}</p>

          <div
            className={`status-badge ${
              userData?.status === "active"
                ? "status-active"
                : "status-inactive"
            }`}
          >
            {userData?.status?.toUpperCase()}
          </div>
        </div>

        {/* Rates */}
        <div className="highlight-cards">
          <div className="highlight-box">
            <TbBottleFilled /> Jar: ₹{userData?.jarPrice}
          </div>
          <div className="highlight-box">
            <GiWaterGallon /> Capsule: ₹{userData?.capsulePrice}
          </div>
        </div>

        {/* Filters */}
        <div className="filter-card">
          <div className="filter-row">
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="filter-select"
            >
              {months.map((m, i) => (
                <option key={i + 1} value={i + 1}>
                  {m}
                </option>
              ))}
            </select>

            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="filter-select"
            >
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>

          {filteredEntries.length > 0 && (
            <button
              onClick={downloadPDF}
              style={{
                padding: "10px 16px",
                background: "linear-gradient(120deg, #022c32, #03b8b3)",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                fontWeight: 600,
                cursor: "pointer",
                fontSize: "14px",
                width: "100%",
              }}
            >
              Download PDF
            </button>
          )}
        </div>

        {/* Entries */}
        <div className="section-title">Daily Usage & Billing</div>

        {filteredEntries.length ? (
          <div className="table-container">
            <table className="history">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Jars</th>
                  <th>Capsules</th>
                  <th>Total (Rs)</th>
                </tr>
              </thead>
              <tbody>
                {filteredEntries.map((e) => (
                  <tr key={e.id}>
                    <td>{e.date}</td>
                    <td>{e.jarQty}</td>
                    <td>{e.capsuleQty}</td>
                    <td>₹{e.totalPrice}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p style={{ textAlign: "center", marginTop: 10, color: "#777" }}>
            No data found for this month.
          </p>
        )}

        {/* Totals */}
        <div className="totals-box">
          Total Jars: {totalJars} <br />
          Total Capsules: {totalCaps} <br />
          Total Amount: ₹{totalAmount.toFixed(2)}
        </div>

        {/* Payment Section */}
        <div className="payment-section">
          <h2
            style={{
              color: "#022c32",
              fontWeight: "800",
              fontSize: "18px",
              marginBottom: "8px",
            }}
          >
            💳 Monthly Payment
          </h2>

          {paymentMode === "UPI" || paymentMode === "CASH" ? (
            <>
              <p
                style={{
                  fontSize: "15px",
                  marginBottom: "12px",
                  color: "#444",
                }}
              >
                Payment Status:
                <b style={{ color: "#03b8b3" }}>
                  {" "}
                  Already Paid by {paymentMode}{" "}
                </b>
              </p>
            </>
          ) : (
            <>
              <p
                style={{
                  fontSize: "15px",
                  marginBottom: "12px",
                  color: "#444",
                }}
              >
                Selected Month Bill:
                <b style={{ color: "#03b8b3" }}> ₹{totalAmount.toFixed(2)} </b>
              </p>

              {totalAmount > 0 &&
                (paymentRequestStatus === null ||
                  paymentRequestStatus === "REJECTED") && (
                  <button
                    onClick={generateQR}
                    style={{
                      marginTop: "8px",
                      background: "linear-gradient(120deg, #03b8b3, #04d4cd)",
                      padding: "12px 20px",
                      borderRadius: "10px",
                      border: "none",
                      color: "#fff",
                      fontWeight: "700",
                      cursor: "pointer",
                      fontSize: "15px",
                      boxShadow: "0 3px 8px rgba(0,0,0,0.2)",
                      width: "100%",
                      maxWidth: "280px",
                    }}
                  >
                    Generate Payment QR
                  </button>
                )}

              {qrUrl && paymentRequestStatus !== "SUCCESS" && (
                <div style={{ marginTop: "20px" }}>
                  <img
                    src={qrUrl}
                    alt="Payment QR"
                    style={{
                      width: "220px",
                      height: "220px",
                      borderRadius: "12px",
                      boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
                    }}
                  />

                  <p
                    style={{
                      marginTop: "10px",
                      fontWeight: "700",
                      fontSize: "16px",
                      color: "#022c32",
                    }}
                  >
                    Pay ₹{qrAmount}
                  </p>

                  {/* ⏳ Pending text */}
                  {paymentRequestStatus === "PENDING" && (
                    <div>
                      <p
                        style={{
                          marginTop: "12px",
                          fontWeight: "700",
                          fontSize: "15px",
                          color: "#ff9800",
                        }}
                      >
                        ⏳ Pending Approval
                      </p>
                      <p>
                        Your payment request is under review by the Owner. Once
                        approved, your payment status will be updated.
                      </p>
                    </div>
                  )}

                  {/* ✅ I Paid button – only when NOT pending */}
                  {(paymentRequestStatus === null ||
                    paymentRequestStatus === "REJECTED") && (
                    <button
                      onClick={markAsPaidRequest}
                      style={{
                        marginTop: "12px",
                        padding: "10px 18px",
                        borderRadius: "8px",
                        border: "none",
                        width: "100%",
                        maxWidth: "220px",
                        fontWeight: "700",
                        cursor: "pointer",
                        background: "linear-gradient(120deg, #022c32, #03b8b3)",
                        color: "#fff",
                      }}
                    >
                      ✅ I Paid
                    </button>
                  )}
                </div>
              )}
            </>
          )}

          {paymentRequestStatus === "REJECTED" && (
            <div
              style={{
                background: "#ffecec",
                color: "#d32f2f",
                padding: "10px",
                borderRadius: "8px",
                fontWeight: "700",
                marginBottom: "12px",
                marginTop: "16px",
              }}
            >
              Payment Rejected by{" "}
              <span style={{ color: "#022c32" }}>Refresh H2O</span>. Please pay
              again.
            </div>
          )}
        </div>
      </div>
    </>
  );
}